import { message } from 'antd';

import { useAuthenticationDataContext } from './AuthenticationDataProvider';
import { useAuthorizationDataContext, Role } from './AuthorizationDataProvider';

import { APP_API } from './EnvVars';

interface HttpRequest {
	args: RequestInit
	isPublicPath?: boolean
	path: string
}

export interface HttpResponse<T> extends Response {
	parsedBody?: T;
	error?: {
		id: number,
		message: string,
	};
}

interface AuthenticationResponse {
	email: string
	firstName: string
	lastName: string
	role: Role 
	permissions: string[]
	refreshToken: string
	rocketAuthToken?: string
	rocketUserId?: string
	token: string
	userId: string
}

const useFetchWrapper = () => {
	const {
		deleteData,
		refreshToken,
		setData: setAuthenticationData,
		token
	} = useAuthenticationDataContext();
	const { setData: setAuthorizationData  } = useAuthorizationDataContext();

	const getBody = (headers: any, body: any) => {

		switch(headers['Content-type']) {
			case 'application/json':
				return JSON.stringify(body);
			default:
				return body;
		}
	}

	const getHeaders = (headers: HeadersInit | undefined, isPublicPath?: boolean): any => {
		if (token && !isPublicPath ) {
			return {
				...headers,
				'X-Authentication-Token': token,
			}
		}
	
		return headers;
	}

	const get = async<T>(
		path: string,
		isPublicPath?: boolean,
		headers = {}
	): Promise<HttpResponse<T>> => {
		const args: RequestInit = {
			headers: getHeaders(headers, isPublicPath),
			method: 'GET',
		};

		return await http<T>({ args, isPublicPath, path });
	};

	const http = async<T>({
		args,
		isPublicPath,
		path,
	}: HttpRequest): Promise<HttpResponse<T>> => {
		const request: RequestInfo = new Request(`${APP_API}${path}`, args);
		const response: HttpResponse<T> = await fetch(request);

		try {

			if (response.ok) {
				const contentType = response.headers.get("content-type");

				if (contentType === 'application/json') {
					response.parsedBody = await response.json();
				}
			}	else {

				if (response.status === 401 && !isPublicPath) {
					const tokenRefreshing = localStorage.getItem('tokenRefreshing');

					if (tokenRefreshing !== 'true') {
						localStorage.setItem('tokenRefreshing', 'true');
						const freshToken = await tryTokenRefresh();

						// If refreshToken is valid and authentication succeded, update token and refresh page
						if (freshToken) {
							localStorage.removeItem('tokenRefreshing');

							// Retry request with the newly generated token
							args.headers = {
								...args.headers,
								'X-Authentication-Token': freshToken,
							}

							const secondTryRequest: RequestInfo = new Request(`${APP_API}${path}`, args);
							const secondTryResponse: HttpResponse<T> = await fetch(secondTryRequest);

							if (secondTryResponse.ok) {
								const contentType = secondTryResponse.headers.get("content-type");
				
								if (contentType === 'application/json') {
									secondTryResponse.parsedBody = await secondTryResponse.json();
								}
							}	else {
								deleteData();

								response.error = await response.json();
								message.error(response.error?.message, 6);
							}

						} else {
							deleteData();

							response.error = await response.json();
							message.error(response.error?.message, 6);
						}
					}
				} else {
					response.error = await response.json();
					message.error(response.error?.message, 6);
				}
			}
		} catch(err) {
			// Return parsed error
			console.log(err);
		}
	
		return response;
	}
	
	const remove = async<T>(
		path: string,
		isPublicPath?: boolean,
		headers = {
			'Content-type': 'application/json',
		},
	): Promise<HttpResponse<T>> => {
		const args: RequestInit = {
			headers: getHeaders(headers, isPublicPath),
			method: 'DELETE',
		}

		return await http<T>({ args, isPublicPath, path });
	};

	const post = async <T>(
		path: string,
		body: any,
		isPublicPath?: boolean,
		headers = {
			'Content-type': 'application/json',
		}
	): Promise<HttpResponse<T>> => {
		const args: RequestInit = {
			body: getBody(headers, body),
			headers: getHeaders(headers, isPublicPath),
			method: 'POST',
		};

		return await http<T>({ args, isPublicPath, path });
	};

	const postFormData = async <T>(
		path: string,
		body: any,
		isPublicPath?: boolean,
	): Promise<HttpResponse<T>> => {
		const args: RequestInit = {
			body: body,
			headers: getHeaders({}, isPublicPath),
			method: 'POST',
		};

		return await http<T>({ args, isPublicPath, path });
	};

	const put = async <T>(
		path: string,
		body: any,
		isPublicPath?: boolean,
		headers = {
			'Content-type': 'application/json',
		},
	): Promise<HttpResponse<T>> => {
		const args: RequestInit = {
			body: getBody(headers, body),
			headers: getHeaders(headers, isPublicPath),
			method: 'PUT',
		};

		return await http<T>({ args, isPublicPath, path });
	};

	const tryTokenRefresh = async () => {
		const res: HttpResponse<AuthenticationResponse> = await fetch(`${APP_API}/user/authenticate`, {
			body: JSON.stringify({ token: refreshToken }),
			headers: {
				'Content-type': 'application/json',
			},
			method: 'PUT',
		});

		if (res.ok) {
			const parsedBody = await res.json();

			const {
				email,
				firstName,
				lastName,
				permissions,
				refreshToken,
				rocketAuthToken,
				rocketUserId,
				role,
				token,
				userId
			} = parsedBody;
			
			setAuthenticationData({
				email,
				firstName,
				lastName,
				refreshToken,
				rocketAuthToken,
				rocketUserId,
				token,
				userId,
			});

			setAuthorizationData({
				permissions,
				role,
			});

			return token;
		}
		
		return '';
	}

	return {
    get,
		post,
		postFormData,
    put,
    remove,
  };

}

export default useFetchWrapper;